import React, { FC } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { IProps } from './SCUIDesignCollectiveDrawerContent.config';
import { Box, Chip, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { SCUIButton } from '../../atoms';
import Swal from 'sweetalert2';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/Twitter';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import HomeIcon from '@mui/icons-material/Home';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { enumCommentStatusFlowChipColor } from '../../../types/enums/status';
import { DesignCollectiveCommand } from '../../../services/command';
import { useTranslation } from 'react-i18next';

/* ── Reusable info row ─────────────────────────────────────────────── */
const InfoRow: FC<{
    icon: React.ReactNode;
    label: string;
    value?: React.ReactNode;
}> = ({ icon, label, value }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-child': { borderBottom: 'none' },
        }}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                minWidth: 148,
                flexShrink: 0,
                color: 'text.secondary',
                pt: 0.1,
            }}
        >
            {icon}
            <Typography variant="caption" fontWeight={700} color="text.secondary">
                {label}
            </Typography>
        </Box>
        <Box sx={{ flex: 1, wordBreak: 'break-word' }}>
            {value ?? (
                <Typography variant="body2" color="text.disabled">
                    —
                </Typography>
            )}
        </Box>
    </Box>
);

/* ── Section header ────────────────────────────────────────────────── */
const SectionHeader: FC<{ label: string }> = ({ label }) => (
    <Box sx={{ pt: 2.5, pb: 1 }}>
        <Typography
            variant="caption"
            fontWeight={700}
            color="text.disabled"
            sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}
        >
            {label}
        </Typography>
    </Box>
);

/* ── Social URL value ───────────────────────────────────────────────── */
const SocialValue: FC<{ url?: string }> = ({ url }) => {
    if (!url || url === '-' || url.trim() === '') {
        return (
            <Typography variant="body2" color="text.disabled">
                —
            </Typography>
        );
    }
    if (url.includes('http')) {
        return (
            <Link
                href={url}
                target="_blank"
                rel="noreferrer"
                underline="hover"
                variant="body2"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, wordBreak: 'break-all' }}
            >
                {url}
                <OpenInNewIcon sx={{ fontSize: 13, flexShrink: 0 }} />
            </Link>
        );
    }
    return <Typography variant="body2">{url}</Typography>;
};

/* ── Main component ─────────────────────────────────────────────────── */
export const SCUIDesignCollectiveDrawerContent: FC<IProps> = ({ detail, onClose, onUpdate }) => {
    const { t } = useTranslation();

    const enumDesignCollectiveStatus: any = {
        1: t('general.waitingForApproval'),
        2: t('general.approved'),
        3: t('general.declined'),
    };

    const [statusName, setStatusName] = React.useState(detail?.statusId);
    const [status, setStatus] = React.useState(enumDesignCollectiveStatus[detail?.statusId]);

    const _handleChangeStatus = (event: SelectChangeEvent) => {
        setStatusName(event.target.value as string);
    };

    const getParams = () => {
        let params: any = {};
        params.designCollectiveId = detail?.id;
        params.status = statusName.toString();
        return params;
    };

    const statuGuncelle = async () => {
        let params: any = getParams();
        if (params.status === '1') params.status = '0';
        DesignCollectiveCommand.PutDesignCollectiveStatusUpdate(params)
            .then(() => {
                setStatus(enumDesignCollectiveStatus[params.status]);
                onUpdate();
                Swal.fire(t('general.updated'), ' ', 'success');
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* ── Sticky header ── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h6" fontWeight={700}>
                        {t('general.detail')}: {detail?.id}
                    </Typography>
                    <Chip
                        label={status}
                        color={enumCommentStatusFlowChipColor[statusName]?.statusType}
                        size="small"
                        sx={{ fontWeight: 600 }}
                    />
                </Box>
                <Tooltip title={t('general.close')}>
                    <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ── Scrollable body ── */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                {/* Contact info */}
                <SectionHeader label={t('general.contactInformation') || 'İletişim Bilgileri'} />
                <Stack spacing={0}>
                    <InfoRow
                        icon={<CalendarTodayIcon sx={{ fontSize: 15 }} />}
                        label={t('general.date')}
                        value={<Typography variant="body2">{detail?.createDate}</Typography>}
                    />
                    <InfoRow
                        icon={<PersonIcon sx={{ fontSize: 15 }} />}
                        label={`${t('general.name')} - ${t('general.surname')}`}
                        value={
                            <Typography variant="body2" fontWeight={500}>
                                {detail?.fullName}
                            </Typography>
                        }
                    />
                    <InfoRow
                        icon={<EmailIcon sx={{ fontSize: 15 }} />}
                        label={t('general.eMail')}
                        value={
                            detail?.email ? (
                                <Link href={`mailto:${detail.email}`} underline="hover" variant="body2">
                                    {detail.email}
                                </Link>
                            ) : undefined
                        }
                    />
                    <InfoRow
                        icon={<PhoneIcon sx={{ fontSize: 15 }} />}
                        label={t('general.phoneNumber')}
                        value={<Typography variant="body2">{detail?.phoneNumber}</Typography>}
                    />
                    <InfoRow
                        icon={<LocationCityIcon sx={{ fontSize: 15 }} />}
                        label={t('general.city')}
                        value={<Typography variant="body2">{detail?.city}</Typography>}
                    />
                    <InfoRow
                        icon={<HomeIcon sx={{ fontSize: 15 }} />}
                        label={t('general.address')}
                        value={<Typography variant="body2">{detail?.address}</Typography>}
                    />
                </Stack>

                {/* Social media */}
                <SectionHeader label={t('general.socialMedia') || 'Sosyal Medya'} />
                <Stack spacing={0}>
                    <InfoRow
                        icon={<InstagramIcon sx={{ fontSize: 15, color: '#E1306C' }} />}
                        label="Instagram"
                        value={<SocialValue url={detail?.instagramUrl} />}
                    />
                    <InfoRow
                        icon={<YouTubeIcon sx={{ fontSize: 15, color: '#FF0000' }} />}
                        label="YouTube"
                        value={<SocialValue url={detail?.youtubeUrl} />}
                    />
                    <InfoRow
                        icon={<TwitterIcon sx={{ fontSize: 15, color: '#1DA1F2' }} />}
                        label="Twitter"
                        value={<SocialValue url={detail?.twitterUrl} />}
                    />
                </Stack>

                {/* Description */}
                <SectionHeader label={t('general.description')} />
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'action.hover',
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 1,
                    }}
                >
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        {detail?.description || <span style={{ color: 'text.disabled' }}>—</span>}
                    </Typography>
                </Box>

                {/* Attachments */}
                {detail?.attachment?.length > 0 && (
                    <>
                        <SectionHeader label={t('general.url') || 'Dosyalar'} />
                        <Stack spacing={0.75}>
                            {detail.attachment.map((attach: any, index: number) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1.25,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                        '&:hover': { bgcolor: 'action.hover' },
                                        transition: 'background-color 0.15s ease',
                                    }}
                                >
                                    <AttachFileIcon sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
                                    <Link
                                        href={attach?.attachmentUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        underline="hover"
                                        variant="body2"
                                        sx={{ flex: 1, wordBreak: 'break-all' }}
                                    >
                                        {attach?.attachmentUrl}
                                    </Link>
                                    <OpenInNewIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
                                </Box>
                            ))}
                        </Stack>
                    </>
                )}
            </Box>

            {/* ── Sticky footer: status update ── */}
            <Box
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexShrink: 0,
                }}
            >
                <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel id="statusSelectLabel">{t('general.operation')}</InputLabel>
                    <Select
                        labelId="statusSelectLabel"
                        id="statusSelect"
                        value={statusName}
                        label={t('general.operation')}
                        onChange={_handleChangeStatus}
                    >
                        <MenuItem value={2}>{t('general.approved')}</MenuItem>
                        <MenuItem value={3}>{t('general.declined')}</MenuItem>
                    </Select>
                </FormControl>
                <SCUIButton
                    text={t('general.update')}
                    type="primary"
                    sizeType="fixed"
                    size={'m'}
                    buttonType="button"
                    onClick={statuGuncelle}
                />
            </Box>
        </Box>
    );
};
